import { RefObject } from 'react';
import { useMutation } from 'react-query';
import axios from 'axios';
import { UploadProgressRef } from '../hooks/useUploadProgress';
import { authFetch } from '../../../../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../../../../CommonUtils/urlWithParams';
import { useFeatureFlag } from '../../../../../../_hooks/useFeatureFlag';
import { Feature } from '../../../../../../CommonSelectors/CommonArea.types';

interface UseUploadAgreementMutationParams {
  isFirm?: boolean;
  uploadProgressRef: RefObject<UploadProgressRef>;
}

interface UploadFileParams {
  file: File;
}

export const useUploadAgreementMutation = ({
  isFirm,
  uploadProgressRef,
}: UseUploadAgreementMutationParams) => {
  const { CancelToken } = axios;
  const source = CancelToken.source();

  const isRedtailImaging = useFeatureFlag(Feature.RedtailImaging);

  return useMutation(async ({ file }: UploadFileParams) => {
    try {
      const uploadUrl = await authFetch(
        urlWithParams(
          `/Adviser/Settings/${isFirm ? 'FirmAgreementUploadUrl' : 'MyAgreementUploadUrl'}`,
          { fileName: file.name },
        ),
      );

      if (uploadProgressRef?.current) {
        uploadProgressRef.current.bindOnCancel(source);
      }

      await axios.request({
        method: 'put',
        url: uploadUrl,
        data: file,
        headers: {
          ...(isRedtailImaging ? {} : { 'x-amz-server-side-encryption': 'AES256' }),
          'Content-Type': file.type,
        },
        cancelToken: source.token,
        onUploadProgress: p => {
          if (uploadProgressRef?.current) {
            uploadProgressRef.current.onUploadProgress(p);
          }
        },
      });

      if (uploadProgressRef?.current) {
        uploadProgressRef.current.onUploadSuccess();
      }
    } catch (err) {
      if (uploadProgressRef?.current) {
        if (axios.isCancel(err)) {
          uploadProgressRef.current.onCancel();
        } else {
          uploadProgressRef.current.onError();
        }
      }
      throw err;
    }
  });
};
